import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../context/OnboardingContext';
import { CheckCircle2, Circle, ChevronUp, ChevronDown, Sparkles } from 'lucide-react';

const OnboardingChecklist = ({ collapsed: sidebarCollapsed, onExpandSidebar }) => {
  const navigate = useNavigate();
  const { checklist, isComplete, doneCount, allDone, loaded, dismissed, dismiss } = useOnboarding();
  const [open, setOpen] = useState(true);

  if (!loaded || !checklist.length || dismissed) return null;

  const pct = checklist.length ? Math.round((doneCount / checklist.length) * 100) : 0;

  // Collapsed sidebar: progress ring that expands the sidebar on click
  if (sidebarCollapsed) {
    return (
      <div className="px-2 pb-3 flex justify-center">
        <button
          onClick={() => onExpandSidebar && onExpandSidebar()}
          title={`Getting Started — ${doneCount}/${checklist.length} — click to expand`}
          className="relative w-10 h-10 rounded-full flex items-center justify-center bg-primary/10 hover:bg-primary/20 transition-colors cursor-pointer"
        >
          <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15" fill="none" stroke="#e5e7eb" strokeWidth="3" />
            <circle cx="18" cy="18" r="15" fill="none" stroke="hsl(var(--primary))" strokeWidth="3"
              strokeDasharray={`${pct * 0.942} 100`} strokeLinecap="round" />
          </svg>
          <span className="absolute text-[9px] font-bold text-primary">{doneCount}/{checklist.length}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 pb-3">
      <div className="rounded-lg border border-primary/20 bg-primary/5 overflow-hidden">
        {/* Header */}
        <button
          className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-primary/10 transition-colors"
          onClick={() => setOpen(o => !o)}
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-semibold text-primary">Getting Started</span>
            <span className="text-[10px] text-primary/70 font-medium">{doneCount}/{checklist.length}</span>
          </div>
          {open
            ? <ChevronUp className="w-3.5 h-3.5 text-primary/60" />
            : <ChevronDown className="w-3.5 h-3.5 text-primary/60" />}
        </button>

        {/* Progress bar */}
        <div className="h-0.5 bg-primary/10">
          <div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${pct}%` }} />
        </div>

        {/* Items */}
        {open && (
          <div className="py-1">
            {checklist.map(item => {
              const done = isComplete(item.key);
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-start gap-2.5 px-3 py-2 text-left hover:bg-primary/10 transition-colors group ${done ? 'opacity-50' : ''}`}
                >
                  <div className="mt-0.5 shrink-0">
                    {done
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                      : <Circle className="w-3.5 h-3.5 text-primary/40 group-hover:text-primary/70" />
                    }
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs font-medium leading-tight ${done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                      {item.label}
                    </p>
                    <p className="text-[10px] text-muted-foreground leading-tight mt-0.5 truncate">
                      {item.description}
                    </p>
                  </div>
                  {!done && <Icon className="w-3 h-3 text-primary/40 group-hover:text-primary shrink-0 mt-0.5 ml-auto" />}
                </button>
              );
            })}

            {/* Dismiss button — always visible at bottom */}
            <div className="px-3 pb-2 pt-1 border-t border-primary/10 mt-1">
              <button
                onClick={dismiss}
                className="w-full text-[10px] text-primary/50 hover:text-primary py-1 transition-colors"
              >
                {allDone ? 'All done! Dismiss this panel' : 'Dismiss'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingChecklist;
